/**
 * Shared currency formatting for CRM money fields (contacts, companies,
 * deals, analytics — all store amounts in integer cents).
 *
 * Falls back to the deployment-level default currency
 * (`featureStore.defaultCurrency`, sourced from `GET /api/features`'s
 * `default_currency`, resolved via `getDefaultCurrency()`) when a record
 * doesn't carry its own currency code.
 */

import { getDefaultCurrency } from '@/hooks/useDefaultCurrency'

/**
 * Format integer cents as a localized currency string.
 * Returns '—' for null/undefined cents. Uses `currency` when given,
 * otherwise the deployment default.
 */
export function formatCents(cents: number | null | undefined, currency?: string | null): string {
  if (cents == null) return '—'
  const code = currency || getDefaultCurrency()
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: code,
      maximumFractionDigits: 0,
    }).format(cents / 100)
  } catch {
    // A non-ISO / malformed currency code makes Intl.NumberFormat throw
    // RangeError. Fall back to a plain formatted number + code suffix so a
    // single bad row value can't crash the whole page.
    return new Intl.NumberFormat(undefined, { maximumFractionDigits: 0 }).format(cents / 100) + ' ' + code
  }
}

/**
 * Order-page money convention (es-MX locale, no decimals, explicit
 * currency-code suffix — e.g. "$150 MXN"). Shared by OrdersPage (order
 * rows, detail totals, stats) and NewOrderModal so every money amount on
 * the Orders page renders through one formatter (SA-49 review finding).
 */
export function formatCurrency(cents: number, currency?: string): string {
  const code = currency || getDefaultCurrency()
  return `$${(cents / 100).toLocaleString('es-MX', { minimumFractionDigits: 0 })} ${code}`
}
